import React from 'react';
import { useTranslation } from 'react-i18next';
import { ACCOUNT_STATUSES } from '@/constants';
import type { AdminUser, AdminUserActionHandlers } from '@/types/adminUsers';
import styles from './styles.module.scss';

export interface UserActionButtonsProps extends AdminUserActionHandlers {
  user: AdminUser;
  /** Layout class for the wrapping row (placement is owned by the parent). */
  className?: string;
}

/**
 * Admin user actions (send verification link / block / unblock) shared by the
 * mobile row card and the desktop table. Renders nothing when no action applies.
 */
export const UserActionButtons: React.FC<UserActionButtonsProps> = ({
  user,
  className,
  pendingActions,
  onSendVerification,
  onBlock,
  onUnblock,
}) => {
  const { t } = useTranslation('admin');

  const canSendLink = !user.email_verified && Boolean(user.email);
  const canBlock = user.account_status === ACCOUNT_STATUSES.ACTIVE;
  const canUnblock = user.account_status === ACCOUNT_STATUSES.BLOCKED;

  if (!canSendLink && !canBlock && !canUnblock) return null;

  const isSending = pendingActions.has(`verify-${user.id}`);
  const isBlocking = pendingActions.has(`block-${user.id}`);
  const isUnblocking = pendingActions.has(`unblock-${user.id}`);

  return (
    <div className={className ? `${styles.row} ${className}` : styles.row}>
      {canSendLink && (
        <button
          type="button"
          className={`${styles.action} ${styles.verify}`}
          onClick={() => onSendVerification(user.id)}
          disabled={isSending}
          title={t('sendVerificationLink')}
          data-testid="admin-user-action-send-link"
        >
          {t('userSendLink')}
        </button>
      )}
      {canBlock && (
        <button
          type="button"
          className={`${styles.action} ${styles.block}`}
          onClick={() => onBlock(user.id)}
          disabled={isBlocking}
          data-testid="admin-user-action-block"
        >
          {isBlocking ? t('userBlocking') : t('userBlock')}
        </button>
      )}
      {canUnblock && (
        <button
          type="button"
          className={`${styles.action} ${styles.unblock}`}
          onClick={() => onUnblock(user.id)}
          disabled={isUnblocking}
          data-testid="admin-user-action-unblock"
        >
          {isUnblocking ? t('userUnblocking') : t('userUnblock')}
        </button>
      )}
    </div>
  );
};

export default UserActionButtons;
